import { FC, useEffect } from "react";
import { useStyles } from "./Auth.styles";
import { Checkbox, Group, Select, Tooltip } from "@mantine/core";
import {
  EMAIL_REGEX,
  InputGroup,
  NAME_REGEX,
} from "@funded-here-interface/common";

interface IProps {
  numOfDirectors: number;
  allDirectorsEmailPresent: boolean;
  directorChecked: boolean;
  setDirectorChecked: (value: boolean) => void;
  error?: { [key: string]: string };
  repEmail: string;
  directorEmails: string[];
  handleAuthDirectorEmailChange: (email: string) => void;
  setRepEmail: (value: string) => void;
  setRepresentativeFirstName: (value: string) => void;
  representativeFirstName: string;
  setRepresentativeLastName: (value: string) => void;
  representativeLastName: string;
  setRepresentativeTitle: (value: string) => void;
  representativeTitle: string;
  onClearError?: (field: string) => void;
}

const Auth: FC<IProps> = (props) => {
  const { classes } = useStyles();
  const {
    numOfDirectors,
    allDirectorsEmailPresent,
    directorChecked,
    setDirectorChecked,
    error,
    repEmail,
    directorEmails,
    handleAuthDirectorEmailChange,
    setRepEmail,
    setRepresentativeFirstName,
    representativeFirstName,
    setRepresentativeLastName,
    representativeLastName,
    setRepresentativeTitle,
    representativeTitle,
    onClearError,
  } = props;

  /**
   * Auto-disable director checkbox when preconditions aren't met
   * Only reset if currently checked to prevent resetting saved state during initial load
   * Also clear rep fields if selected director was removed
   */
  useEffect(() => {
    if (directorChecked) {
      if (numOfDirectors < 1 || !allDirectorsEmailPresent) {
        setDirectorChecked(false);
        setRepEmail("");
        setRepresentativeFirstName("");
        setRepresentativeLastName("");
        setRepresentativeTitle("");
      }
    }
  }, [numOfDirectors, allDirectorsEmailPresent, directorChecked]);

  const canSelectDirector = numOfDirectors >= 1 && allDirectorsEmailPresent;

  return (
    <>
      <Group style={{ maxWidth: "640px", width: "100%" }}>
        <p className={classes.authTitle}>Authorized Representative</p>
        <Tooltip
          label="Requires at least 1 Director"
          disabled={canSelectDirector}
          styles={{ tooltip: { cursor: "default" } }}
        >
          <div>
            <Checkbox
              disabled={!canSelectDirector}
              checked={directorChecked}
              labelPosition="left"
              label="Is the authorized representative one of the above directors?"
              onChange={(event) =>
                setDirectorChecked(event.currentTarget.checked)
              }
            />
          </div>
        </Tooltip>
      </Group>
      {directorChecked && (
        <Select
          error={error?.repEmail}
          label="Email"
          value={repEmail}
          placeholder="Select Director"
          data={directorEmails}
          onChange={handleAuthDirectorEmailChange}
          styles={{ input: { height: "42px", marginTop: "3px" } }}
        />
      )}
      {!directorChecked && (
        <>
          <InputGroup
            isErrored={error?.repEmail}
            onChange={(value: string) => {
              setRepEmail(value.replace(EMAIL_REGEX, "").trim());
              onClearError?.("repEmail");
            }}
            labelText="Email"
            id="repEmail"
            value={repEmail}
            errorMessage={!!error?.repEmail}
          />
          <InputGroup
            isErrored={error?.repFirstName}
            onChange={(value: string) => {
              setRepresentativeFirstName(value.replace(NAME_REGEX, ""));
              onClearError?.("repFirstName");
            }}
            labelText="First / given name (as shown in the national identification document)"
            value={representativeFirstName}
            id="representative_first_name"
            errorMessage={!!error?.repFirstName}
          />
          <InputGroup
            isErrored={error?.repLastName}
            onChange={(value: string) => {
              setRepresentativeLastName(value.replace(NAME_REGEX, ""));
              onClearError?.("repLastName");
            }}
            labelText="Last name (as shown in the national identification document)"
            value={representativeLastName}
            id="representative_last_name"
            errorMessage={!!error?.repLastName}
          />
          <InputGroup
            isErrored={error?.repTitle}
            onChange={(value: string) => {
              setRepresentativeTitle(value.replace(NAME_REGEX, ""));
              onClearError?.("repTitle");
            }}
            labelText="Role in the company"
            id="representative_title"
            value={representativeTitle}
            errorMessage={!!error?.repTitle}
          />
        </>
      )}
    </>
  );
};

export default Auth;
